import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CampaignCta from "@/components/CampaignCta";
import FaqAccordion, { type FaqAccordionItem } from "@/components/FaqAccordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { trackCtaClick } from "@/lib/analytics";
import { RESOURCES_ENABLED, RESOURCE_ASSETS } from "@/config/resources";
import {
  EyeOff,
  Undo2,
  ClipboardList,
  ShieldCheck,
  GitCompare,
  Search,
  KeyRound,
  CalendarClock,
  RotateCcw,
  CheckCircle2,
  XCircle,
} from "lucide-react";

const itChecklist = RESOURCE_ASSETS.it_checklist;

const problems = [
  {
    icon: <EyeOff className="h-8 w-8 text-primary" />,
    title: "Jotform is shadow SaaS",
    text: "Forms run critical workflows but rarely sit inside IT's backup policy.",
  },
  {
    icon: <Undo2 className="h-8 w-8 text-primary" />,
    title: "Native SaaS has no real undo",
    text: "One bad edit and a business-critical form is gone.",
  },
  {
    icon: <ClipboardList className="h-8 w-8 text-primary" />,
    title: "Audit questions get awkward",
    text: '"When did this form change?" needs an answer you can show.',
  },
];

const policyFit = [
  {
    icon: <ShieldCheck className="h-7 w-7 text-primary" />,
    text: "Fits alongside your Microsoft 365, Google Workspace, and Salesforce backups in your SaaS backup policy.",
  },
  {
    icon: <GitCompare className="h-7 w-7 text-primary" />,
    text: "Supports change-management review with daily version history.",
  },
  {
    icon: <Search className="h-7 w-7 text-primary" />,
    text: 'Helps internal audit answer "what changed, and when."',
  },
];

const steps = [
  {
    icon: <KeyRound className="h-8 w-8 text-primary" />,
    title: "Connect",
    text: "Connect with a Jotform API token — no agents, no installs.",
  },
  {
    icon: <CalendarClock className="h-8 w-8 text-primary" />,
    title: "Snapshot",
    text: "Daily automated snapshots run in the background.",
  },
  {
    icon: <RotateCcw className="h-8 w-8 text-primary" />,
    title: "Restore",
    text: "Restore any prior form version in one click.",
  },
];

// NOTE: Security/compliance answers below are PLACEHOLDERS. Do not assert
// SOC 2, data residency, or DPA terms until confirmed. See LAUNCH_CHECKLIST.md.
const faqItems: FaqAccordionItem[] = [
  {
    // TODO(security-faq): confirm SOC 2 status before publishing.
    question: "Is FAB SOC 2 certified?",
    answer:
      "We can share our current security posture and roadmap on request — contact us at support@fab.day.",
  },
  {
    // TODO(security-faq): confirm data storage / residency before publishing.
    question: "Where is data stored?",
    answer:
      "We can share data storage and residency details on request — contact us at support@fab.day.",
  },
  {
    question: "Does FAB back up submissions?",
    answer:
      "No. FAB backs up form configuration only — fields, conditions, logic, and theme.",
  },
  {
    question: "Is FAB affiliated with Jotform?",
    answer:
      "No. FAB is an independent third-party tool that uses the public Jotform API.",
  },
  {
    // TODO(security-faq): confirm DPA availability/terms before publishing.
    question: "Can we get a DPA?",
    answer:
      "We can discuss a data processing agreement on request — contact us at support@fab.day.",
  },
  {
    question: "How does pricing scale across multiple Jotform accounts?",
    answer:
      "Each FAB workspace connects to one Jotform account, with plans matched to that Jotform plan. For multi-account or fleet setups, contact us at support@fab.day.",
  },
];

const ItCompliancePage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      <main className="flex-grow">
        {/* Hero */}
        <section className="bg-gradient-to-br from-primary to-primary/80 dark:from-primary/90 dark:to-primary/70 text-white py-20 md:py-28">
          <div className="container mx-auto px-6 max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight">
              Bring Jotform into your SaaS backup policy.
            </h1>
            <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed text-white/90">
              FAB is the independent backup, version history, and recovery layer
              for Jotform — built for IT teams that need shadow SaaS under control.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 justify-center">
              <CampaignCta
                location="it-hero"
                className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-8"
              >
                Start free 14-day trial
              </CampaignCta>
              {RESOURCES_ENABLED ? (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 dark:border-gray-700"
                >
                  <Link
                    to={itChecklist.routePath}
                    onClick={() =>
                      trackCtaClick("it-hero-checklist", itChecklist.routePath)
                    }
                  >
                    Download the IT backup checklist
                  </Link>
                </Button>
              ) : (
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white text-white hover:bg-white/10 dark:border-gray-700"
                >
                  <a
                    href="#how-it-works"
                    onClick={() => trackCtaClick("it-hero-secondary", "#how-it-works")}
                  >
                    See how it works
                  </a>
                </Button>
              )}
            </div>
            <p className="mt-6 text-xs text-white/70">
              Independent third-party tool. Not affiliated with Jotform.
            </p>
          </div>
        </section>

        {/* Problem block */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <div className="grid gap-6 md:grid-cols-3">
              {problems.map((item) => (
                <Card key={item.title}>
                  <CardHeader>
                    {item.icon}
                    <CardTitle className="text-xl">{item.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-muted-foreground">{item.text}</CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What FAB covers — honesty block (required, not softened) */}
        <section className="py-16 md:py-20 bg-muted/30">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              What FAB covers
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-lg border bg-background p-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-6 w-6 text-green-500" />
                  <h3 className="text-lg font-semibold text-foreground">
                    What FAB backs up
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  Form configuration — fields, conditions, logic, and theme —
                  captured daily.
                </p>
              </div>
              <div className="rounded-lg border bg-background p-6">
                <div className="flex items-center gap-2 mb-3">
                  <XCircle className="h-6 w-6 text-red-500" />
                  <h3 className="text-lg font-semibold text-foreground">
                    What FAB does not back up
                  </h3>
                </div>
                <p className="text-muted-foreground">
                  Form submissions and respondent data. Those stay in Jotform.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Policy fit */}
        <section className="py-16 md:py-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              How FAB fits your policy
            </h2>
            <div className="grid gap-6 md:grid-cols-3">
              {policyFit.map((item) => (
                <div key={item.text} className="flex gap-3">
                  <div className="flex-shrink-0">{item.icon}</div>
                  <p className="text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="py-16 md:py-20 bg-muted/30 scroll-mt-20">
          <div className="container mx-auto px-6 max-w-5xl">
            <h2 className="text-3xl font-bold text-foreground text-center mb-12">
              How it works
            </h2>
            <div className="grid gap-8 md:grid-cols-3">
              {steps.map((item, index) => (
                <div key={item.title} className="text-center px-4">
                  <div className="flex justify-center mb-4">{item.icon}</div>
                  <div className="text-sm font-semibold text-primary mb-1">
                    Step {index + 1}
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Resource CTA band */}
        <section className="py-14 bg-primary dark:bg-primary/90 text-white border-y">
          <div className="container mx-auto px-6 max-w-3xl text-center">
            {RESOURCES_ENABLED ? (
              <>
                <h2 className="text-2xl md:text-3xl font-bold mb-6">
                  Get the IT SaaS backup checklist.
                </h2>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white border-white dark:border-gray-700"
                >
                  <Link
                    to={itChecklist.routePath}
                    onClick={() =>
                      trackCtaClick("it-resource-band", itChecklist.routePath)
                    }
                  >
                    Download the checklist
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  Close the Jotform gap today.
                </h2>
                <p className="text-white/80 mb-6 text-sm">
                  The IT SaaS backup checklist is coming soon.
                </p>
                <CampaignCta
                  location="it-resource-band"
                  className="bg-white text-primary hover:bg-gray-100 dark:bg-gray-900 dark:text-white dark:hover:bg-gray-800 font-semibold px-8"
                >
                  Start free 14-day trial
                </CampaignCta>
              </>
            )}
          </div>
        </section>

        {/* FAQ */}
        <FaqAccordion
          title="IT and compliance questions"
          description="Straight answers for backup-policy, change-management, and audit reviews."
          items={faqItems}
        />

        {/* Final CTA band */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-foreground">
              Close the Jotform gap in your backup policy.
            </h2>
            <CampaignCta location="it-footer">Start free 14-day trial</CampaignCta>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ItCompliancePage;
